// Single source of truth for reading and mutating the producer's saved
// crew ("My Team"). Both the single add/edit dialog and the bulk
// paste-grid drive these mutations so insert/update/delete behaviour
// stays consistent. Writes go straight to the RLS-protected
// `team_members` table (no edge function) — matching the existing
// pattern — and the producer_id is taken from the authenticated user.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  buildTeamMemberPayload,
  findTeamMemberDuplicate,
  normalizeGear,
  type DuplicateReason,
  type FormState,
  type TeamMember,
} from "@/lib/teamMembers";

const DUPLICATE_MARKER = "team_members_producer_email_unique";

/** Thrown when an add-to-team candidate already exists (email OR name). */
export class DuplicateTeamMemberError extends Error {
  constructor(public reason: DuplicateReason) {
    super(`Duplicate team member (${reason})`);
    this.name = "DuplicateTeamMemberError";
  }
}

/** One row to persist in a bulk save, with its chosen duplicate action. */
export interface BulkSaveRow {
  form: FormState;
  /** "insert" for new members; "update" to overwrite an existing one. */
  mode: "insert" | "update";
}

export interface BulkSaveResult {
  inserted: number;
  updated: number;
}

export const useTeamMembers = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const producerId = user?.id;

  const membersQuery = useQuery({
    queryKey: ["team-members", producerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("team_members")
        .select("*")
        .eq("producer_id", producerId!)
        .order("name");
      if (error) throw error;
      return (data || []).map((m) => ({
        ...m,
        gear: Array.isArray(m.gear)
          ? normalizeGear(m.gear as Array<string | { type?: string; detail?: string }>)
          : [],
      })) as TeamMember[];
    },
    enabled: !!producerId,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["team-members"] });

  const saveMutation = useMutation({
    mutationFn: async ({ form, editingId }: { form: FormState; editingId: string | null }) => {
      const payload = buildTeamMemberPayload(producerId!, form);
      if (editingId) {
        const { error } = await supabase.from("team_members").update(payload).eq("id", editingId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("team_members").insert(payload);
        if (error) throw error;
      }
      return { editingId };
    },
    onSuccess: ({ editingId }) => {
      invalidate();
      toast.success(editingId ? "Member updated" : "Member added");
    },
    onError: (err: Error) => {
      const isDuplicate = err?.message?.includes(DUPLICATE_MARKER);
      toast.error(
        isDuplicate ? "A team member with this email already exists." : err.message || "Error",
      );
    },
  });

  const bulkSaveMutation = useMutation({
    mutationFn: async (rows: BulkSaveRow[]): Promise<BulkSaveResult> => {
      const inserts = rows.filter((r) => r.mode === "insert");
      const updates = rows.filter((r) => r.mode === "update");

      // New members: one batched insert (atomic). Pre-validation already
      // removed in-batch duplicates and rows that exist are routed to
      // updates, so the unique constraint shouldn't fire here.
      if (inserts.length) {
        const payloads = inserts.map((r) => buildTeamMemberPayload(producerId!, r.form));
        const { error } = await supabase.from("team_members").insert(payloads);
        if (error) throw error;
      }

      // Existing members chosen for "update": matched by (producer_id,
      // email) since that's the unique key the producer is overwriting.
      for (const r of updates) {
        const payload = buildTeamMemberPayload(producerId!, r.form);
        const { error } = await supabase
          .from("team_members")
          .update(payload)
          .eq("producer_id", producerId!)
          .eq("email", payload.email);
        if (error) throw error;
      }

      return { inserted: inserts.length, updated: updates.length };
    },
    onSuccess: ({ inserted, updated }) => {
      invalidate();
      const parts: string[] = [];
      if (inserted) parts.push(`${inserted} added`);
      if (updated) parts.push(`${updated} updated`);
      toast.success(parts.length ? parts.join(", ") : "No changes");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Bulk add failed");
    },
  });

  // Quick-add a single crew member (from the briefing crew editor) to My
  // Team. Blocks when the email OR name already exists — the DB only
  // enforces email uniqueness, so the name guard is checked here against
  // the cached team. Reuses the same insert path as the dialog so My Team
  // writes stay consistent.
  const addToTeamMutation = useMutation({
    mutationFn: async (form: FormState) => {
      const existing = membersQuery.data ?? [];
      const duplicate = findTeamMemberDuplicate(existing, { name: form.name, email: form.email });
      if (duplicate) throw new DuplicateTeamMemberError(duplicate);
      const { error } = await supabase
        .from("team_members")
        .insert(buildTeamMemberPayload(producerId!, form));
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Added to My Team");
    },
    onError: (err: Error) => {
      if (err instanceof DuplicateTeamMemberError) {
        toast.error(
          err.reason === "email"
            ? "This email is already in My Team."
            : "This name is already in My Team.",
        );
        return;
      }
      // Backstop: the DB unique constraint fired (e.g. cache was stale).
      const isDuplicate = err?.message?.includes(DUPLICATE_MARKER);
      toast.error(
        isDuplicate ? "A team member with this email already exists." : err.message || "Error",
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("team_members").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Member removed");
    },
    onError: (err: Error) => toast.error(err.message || "Error"),
  });

  return { membersQuery, saveMutation, bulkSaveMutation, addToTeamMutation, deleteMutation };
};
